import { useState } from 'react';
import { useApp } from '../App';
import BackButton from '../components/BackButton';
import { useI18n } from '../i18n/index.jsx';

const DOCUMENTS = [
  {
    id: 'regulamin',
    name: 'Regulamin platformy',
    icon: '📜',
    desc: 'Warunki korzystania z WORKIFYPL',
  },
  {
    id: 'license',
    name: 'Umowa licencyjna',
    icon: '📋',
    desc: 'Licencja na korzystanie z oprogramowania',
  },
  {
    id: 'privacy',
    name: 'Polityka prywatności',
    icon: '🔒',
    desc: 'RODO i ochrona danych osobowych',
  },
  {
    id: 'license_author',
    name: 'Umowa licencyjna (prawa autorskie)',
    icon: '©️',
    desc: 'Załącznik do umowy o dzieło',
  },
];

export default function LegalDocuments() {
  const { user } = useApp();
  const { t } = useI18n();
  const [selectedDoc, setSelectedDoc] = useState(null);

  function getDocumentContent(docId) {
    const today = new Date().toLocaleDateString('pl-PL');
    
    switch (docId) {
      case 'regulamin':
        return REGULAMIN_CONTENT;
      case 'license':
        return LICENSE_CONTENT;
      case 'privacy':
        return PRIVACY_CONTENT;
      case 'license_author':
        return LICENSE_AUTHOR_CONTENT;
      default:
        return '';
    }
  }

  function downloadPdf(doc) {
    // In real app, generate PDF on backend
    const content = getDocumentContent(doc.id);
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${doc.id}_workifypl.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (selectedDoc) {
    const content = getDocumentContent(selectedDoc.id);
    return (
      <div className="page">
        <BackButton onClick={() => setSelectedDoc(null)} />
        <h1>{selectedDoc.icon} {selectedDoc.name}</h1>

        <div style={{
          display: 'flex',
          gap: 8,
          marginBottom: 16,
        }}>
          <button
            className="btn"
            style={{ flex: 1, background: 'rgba(99,102,241,0.3)' }}
            onClick={() => downloadPdf(selectedDoc)}
          >
            📥 Pobierz TXT
          </button>
          <button
            className="btn"
            style={{ flex: 1, background: 'rgba(16,185,129,0.3)' }}
            onClick={() => {
              navigator.clipboard.writeText(content);
              alert('✅ Skopiowano do schowka');
            }}
          >
            📋 Kopiuj
          </button>
        </div>

        <div style={{
          background: 'rgba(255,255,255,0.05)',
          borderRadius: 12,
          padding: 16,
          maxHeight: '60vh',
          overflow: 'auto',
        }}>
          <pre style={{
            whiteSpace: 'pre-wrap',
            fontFamily: 'inherit',
            fontSize: 12,
            lineHeight: 1.6,
            margin: 0,
          }}>
            {content}
          </pre>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <BackButton />
      <h1>📜 Dokumenty prawne</h1>

      {/* Info */}
      <div style={{
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        borderRadius: 12,
        padding: 16,
        marginBottom: 16,
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: 8 }}>⚖️ Dokumentacja prawna</div>
        <p style={{ fontSize: 13, opacity: 0.9, margin: 0 }}>
          Regulamin, umowy licencyjne i polityka prywatności platformy WORKIFYPL.
        </p>
      </div>

      {/* Document List */}
      {DOCUMENTS.map(doc => (
        <div
          key={doc.id}
          onClick={() => setSelectedDoc(doc)}
          style={{
            background: 'rgba(255,255,255,0.05)',
            borderRadius: 12,
            padding: 16,
            marginBottom: 8,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <span style={{ fontSize: 32 }}>{doc.icon}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 'bold' }}>{doc.name}</div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>{doc.desc}</div>
          </div>
          <span style={{ opacity: 0.5 }}>→</span>
        </div>
      ))}

      {/* Warning */}
      <div style={{
        background: 'rgba(245,158,11,0.1)',
        borderRadius: 12,
        padding: 16,
        marginTop: 24,
        fontSize: 13,
      }}>
        ⚠️ <strong>Uwaga:</strong> Dokumenty mają charakter wzorcowy. 
        Zalecamy konsultację z prawnikiem przed użyciem w działalności gospodarczej.
      </div>
    </div>
  );
}

// ============================================
// REGULAMIN PLATFORMY WORKIFYPL
// ============================================
const REGULAMIN_CONTENT = `REGULAMIN PLATFORMY WORKIFYPL
========================================

Obowiązuje od: ${new Date().toLocaleDateString('pl-PL')}
Wersja: 1.0

§1. POSTANOWIENIA OGÓLNE

1. Niniejszy Regulamin określa zasady korzystania z platformy internetowej WORKIFYPL 
   dostępnej pod adresem https://workifypl.com (dalej: "Platforma").

2. Właścicielem i operatorem Platformy jest:
   WORKIFYPL SP. Z O.O.
   ul. [Adres siedziby]
   NIP: [NIP]
   KRS: [KRS]
   (dalej: "Operator")

3. Kontakt z Operatorem możliwy jest poprzez:
   - Email: support@workifypl.com
   - Formularz kontaktowy na Platformie

4. Korzystanie z Platformy oznacza akceptację niniejszego Regulaminu.

§2. DEFINICJE

1. "Użytkownik" - osoba fizyczna, osoba prawna lub jednostka organizacyjna 
   korzystająca z Platformy.

2. "Konto" - indywidualne konto Użytkownika na Platformie.

3. "Usługi" - funkcjonalności udostępniane przez Platformę, w tym:
   a) Wystawianie faktur VAT
   b) Zarządzanie dokumentacją księgową
   c) Obsługa umów cywilnoprawnych
   d) Wnioski o pozwolenia na pracę
   e) Rozliczenia i wypłaty

4. "Gateway Contract" - umowa dostępu do Platformy.

5. "Work Contract" - umowa o świadczenie usług pracy.

§3. REJESTRACJA I KONTO

1. Rejestracja na Platformie wymaga:
   a) Podania prawdziwych danych osobowych
   b) Weryfikacji numeru telefonu (Telegram)
   c) Akceptacji niniejszego Regulaminu
   d) Akceptacji Polityki Prywatności

2. Użytkownik zobowiązuje się do:
   a) Podania prawdziwych i aktualnych danych
   b) Aktualizacji danych w przypadku ich zmiany
   c) Nieudostępniania Konta osobom trzecim
   d) Zachowania poufności danych dostępowych

3. Operator zastrzega sobie prawo do:
   a) Weryfikacji tożsamości Użytkownika
   b) Żądania dokumentów potwierdzających dane
   c) Zawieszenia lub usunięcia Konta w przypadku naruszenia Regulaminu

§4. USŁUGI PLATFORMY

1. Platforma umożliwia:
   a) Rejestrację działalności gospodarczej w Polsce
   b) Wystawianie i zarządzanie fakturami VAT
   c) Obsługę umów o dzieło i umów zlecenie
   d) Składanie wniosków o oświadczenia i zezwolenia na pracę
   e) Rozliczenia finansowe i wypłaty środków
   f) Prowadzenie uproszczonej księgowości

2. Usługi są świadczone odpłatnie zgodnie z cennikiem dostępnym na Platformie.

3. Aktualne opłaty:
   a) Gateway Contract: 1200 PLN (jednorazowo) + 350 PLN (miesięcznie)
   b) Prowizja od wypłat: 6%
   c) Oświadczenie o powierzeniu pracy: 400 PLN (opłata urzędowa)

§5. PŁATNOŚCI I ROZLICZENIA

1. Płatności na Platformie realizowane są poprzez:
   a) Przelew bankowy
   b) Kryptowaluty (USDT TRC20)
   c) Saldo wewnętrzne Platformy

2. Wypłaty środków:
   a) Minimalna kwota wypłaty: 100 PLN
   b) Czas realizacji: do 3 dni roboczych
   c) Prowizja: 6% od kwoty wypłaty

3. Operator wystawia faktury VAT za świadczone usługi.

§6. ODPOWIEDZIALNOŚĆ

1. Operator nie ponosi odpowiedzialności za:
   a) Treści wprowadzane przez Użytkowników
   b) Decyzje urzędów i organów państwowych
   c) Przerwy techniczne w działaniu Platformy
   d) Szkody wynikające z nieprawidłowego użytkowania Platformy
   e) Działania osób trzecich

2. Użytkownik ponosi pełną odpowiedzialność za:
   a) Prawdziwość podanych danych
   b) Treść wystawianych dokumentów
   c) Zgodność działań z obowiązującym prawem
   d) Terminowe regulowanie zobowiązań podatkowych

3. Całkowita odpowiedzialność Operatora jest ograniczona do wysokości 
   opłat uiszczonych przez Użytkownika w okresie ostatnich 12 miesięcy.

§7. OCHRONA DANYCH OSOBOWYCH

1. Administratorem danych osobowych jest Operator.

2. Dane osobowe przetwarzane są zgodnie z:
   a) Rozporządzeniem RODO (UE 2016/679)
   b) Polską ustawą o ochronie danych osobowych
   c) Polityką Prywatności Platformy

3. Użytkownik ma prawo do:
   a) Dostępu do swoich danych
   b) Sprostowania danych
   c) Usunięcia danych (prawo do bycia zapomnianym)
   d) Przenoszenia danych
   e) Wniesienia skargi do organu nadzorczego (UODO)

§8. WŁASNOŚĆ INTELEKTUALNA

1. Platforma, jej kod źródłowy, design, logo i treści są własnością Operatora 
   i podlegają ochronie prawnej.

2. Użytkownik otrzymuje niewyłączną, nieprzenoszalną licencję na korzystanie 
   z Platformy zgodnie z jej przeznaczeniem.

3. Zabronione jest:
   a) Kopiowanie elementów Platformy
   b) Dekompilacja kodu źródłowego
   c) Wykorzystywanie znaków towarowych Operatora
   d) Tworzenie dzieł pochodnych

§9. REKLAMACJE

1. Reklamacje należy składać:
   a) Poprzez formularz na Platformie
   b) Na adres email: support@workifypl.com

2. Reklamacja powinna zawierać:
   a) Dane Użytkownika
   b) Opis problemu
   c) Oczekiwany sposób rozwiązania

3. Operator rozpatruje reklamacje w terminie 14 dni roboczych.

§10. ROZWIĄZANIE UMOWY

1. Użytkownik może w każdej chwili:
   a) Zaprzestać korzystania z Platformy
   b) Usunąć swoje Konto

2. Operator może rozwiązać umowę w przypadku:
   a) Naruszenia Regulaminu
   b) Podania nieprawdziwych danych
   c) Działań na szkodę Operatora lub innych Użytkowników
   d) Wykorzystywania Platformy niezgodnie z przeznaczeniem

3. Po rozwiązaniu umowy:
   a) Użytkownik traci dostęp do Platformy
   b) Dane są przechowywane zgodnie z wymogami prawa (min. 5 lat)
   c) Niewypłacone środki podlegają zwrotowi (po potrąceniu należności)

§11. ZMIANY REGULAMINU

1. Operator zastrzega sobie prawo do zmiany Regulaminu.

2. O zmianach Użytkownicy będą informowani:
   a) Poprzez email
   b) Poprzez powiadomienie na Platformie

3. Zmiany wchodzą w życie po 14 dniach od ogłoszenia.

4. Dalsze korzystanie z Platformy oznacza akceptację zmian.

§12. POSTANOWIENIA KOŃCOWE

1. Regulamin podlega prawu polskiemu.

2. Wszelkie spory będą rozstrzygane przez sądy właściwe dla siedziby Operatora.

3. Jeśli jakiekolwiek postanowienie Regulaminu okaże się nieważne, 
   pozostałe postanowienia pozostają w mocy.

4. Regulamin wchodzi w życie z dniem publikacji na Platformie.


========================================
WORKIFYPL SP. Z O.O.
Data publikacji: ${new Date().toLocaleDateString('pl-PL')}
`;

// ============================================
// UMOWA LICENCYJNA NA OPROGRAMOWANIE
// ============================================
const LICENSE_CONTENT = `UMOWA LICENCYJNA NA OPROGRAMOWANIE
========================================

WORKIFYPL - Platforma do zarządzania działalnością gospodarczą

Wersja: 1.0
Data: ${new Date().toLocaleDateString('pl-PL')}

WAŻNE - PRZECZYTAJ UWAŻNIE

Niniejsza Umowa Licencyjna ("Umowa") stanowi prawnie wiążącą umowę między Tobą 
("Licencjobiorca") a WORKIFYPL SP. Z O.O. ("Licencjodawca") dotyczącą korzystania 
z platformy WORKIFYPL ("Oprogramowanie").

§1. UDZIELENIE LICENCJI

1.1. Licencjodawca udziela Licencjobiorcy niewyłącznej, nieprzenoszalnej, 
     odwołalnej licencji na korzystanie z Oprogramowania.

1.2. Licencja obejmuje:
     a) Dostęp do funkcjonalności Platformy przez przeglądarkę internetową
     b) Korzystanie z aplikacji mobilnej (Telegram Mini App)
     c) Dostęp do API w ramach dozwolonego użytku

1.3. Licencja jest udzielana na czas nieokreślony, z zastrzeżeniem warunków 
     rozwiązania określonych w niniejszej Umowie.

§2. OGRANICZENIA LICENCJI

2.1. Licencjobiorca NIE MOŻE:
     a) Kopiować, modyfikować ani tworzyć dzieł pochodnych Oprogramowania
     b) Dekompilować, dezasemblować ani poddawać inżynierii wstecznej
     c) Wynajmować, wydzierżawiać ani sublicencjonować Oprogramowania
     d) Usuwać oznaczeń o prawach autorskich
     e) Wykorzystywać Oprogramowania do celów niezgodnych z prawem
     f) Próbować uzyskać nieautoryzowany dostęp do systemów Licencjodawcy

2.2. Licencjobiorca zobowiązuje się do:
     a) Korzystania z Oprogramowania zgodnie z jego przeznaczeniem
     b) Przestrzegania obowiązujących przepisów prawa
     c) Nienaruszania praw osób trzecich

§3. WŁASNOŚĆ INTELEKTUALNA

3.1. Oprogramowanie, w tym jego kod źródłowy, architektura, design, 
     interfejs użytkownika i dokumentacja, stanowią własność Licencjodawcy.

3.2. Niniejsza Umowa nie przenosi na Licencjobiorcę żadnych praw własności 
     intelektualnej do Oprogramowania.

3.3. Wszelkie nazwy, loga i znaki towarowe są własnością Licencjodawcy.

§4. DANE UŻYTKOWNIKA

4.1. Licencjobiorca zachowuje wszelkie prawa do danych wprowadzonych 
     do Oprogramowania.

4.2. Licencjodawca przetwarza dane zgodnie z Polityką Prywatności.

4.3. Licencjobiorca udziela Licencjodawcy licencji na przetwarzanie danych 
     w zakresie niezbędnym do świadczenia usług.

§5. WYŁĄCZENIE GWARANCJI

5.1. Oprogramowanie jest dostarczane "TAK JAK JEST" (AS IS) bez jakichkolwiek 
     gwarancji, wyraźnych lub dorozumianych.

5.2. Licencjodawca nie gwarantuje:
     a) Nieprzerwanego działania Oprogramowania
     b) Braku błędów
     c) Bezpieczeństwa danych
     d) Przydatności do określonego celu

§6. OGRANICZENIE ODPOWIEDZIALNOŚCI

6.1. W maksymalnym zakresie dozwolonym przez prawo, Licencjodawca nie ponosi 
     odpowiedzialności za jakiekolwiek szkody:
     a) Bezpośrednie, pośrednie, przypadkowe lub wtórne
     b) Utratę danych, zysków lub możliwości biznesowych
     c) Przerwy w działalności

6.2. Całkowita odpowiedzialność Licencjodawcy jest ograniczona do kwoty 
     opłat uiszczonych przez Licencjobiorcę w okresie ostatnich 12 miesięcy.

§7. OKRES OBOWIĄZYWANIA I ROZWIĄZANIE

7.1. Umowa obowiązuje od momentu rozpoczęcia korzystania z Oprogramowania.

7.2. Licencjobiorca może rozwiązać Umowę w dowolnym momencie poprzez 
     zaprzestanie korzystania z Oprogramowania.

7.3. Licencjodawca może rozwiązać Umowę ze skutkiem natychmiastowym 
     w przypadku naruszenia jej warunków.

7.4. Po rozwiązaniu Umowy:
     a) Licencja wygasa
     b) Licencjobiorca traci dostęp do Oprogramowania
     c) Dane są przetwarzane zgodnie z Polityką Prywatności

§8. POSTANOWIENIA KOŃCOWE

8.1. Umowa podlega prawu polskiemu.

8.2. Wszelkie spory będą rozstrzygane przez sądy polskie właściwe 
     dla siedziby Licencjodawcy.

8.3. Nieważność pojedynczych postanowień nie wpływa na ważność całej Umowy.

8.4. Licencjodawca zastrzega sobie prawo do zmiany warunków Umowy.


Korzystając z Oprogramowania, potwierdzasz, że przeczytałeś, zrozumiałeś 
i akceptujesz warunki niniejszej Umowy Licencyjnej.

========================================
WORKIFYPL SP. Z O.O.
`;

// ============================================
// POLITYKA PRYWATNOŚCI
// ============================================
const PRIVACY_CONTENT = `POLITYKA PRYWATNOŚCI
========================================

WORKIFYPL SP. Z O.O.
Obowiązuje od: ${new Date().toLocaleDateString('pl-PL')}

§1. ADMINISTRATOR DANYCH

Administratorem Twoich danych osobowych jest:
WORKIFYPL SP. Z O.O.
ul. [Adres siedziby]
NIP: [NIP], KRS: [KRS]
Email: privacy@workifypl.com

§2. JAKIE DANE ZBIERAMY

2.1. Dane podawane przez Użytkownika:
     - Imię i nazwisko
     - PESEL
     - Numer dokumentu tożsamości
     - Adres zamieszkania
     - Numer telefonu
     - Adres email
     - Numer rachunku bankowego
     - Dane firmowe (NIP, REGON, KRS)

2.2. Dane zbierane automatycznie:
     - Adres IP
     - Identyfikator urządzenia
     - Dane o przeglądarce
     - Logi aktywności
     - Pliki cookies

§3. CELE PRZETWARZANIA

Twoje dane przetwarzamy w celu:

a) Świadczenia usług Platformy (art. 6 ust. 1 lit. b RODO)
b) Wypełnienia obowiązków prawnych (art. 6 ust. 1 lit. c RODO)
c) Realizacji prawnie uzasadnionych interesów (art. 6 ust. 1 lit. f RODO)
d) Na podstawie zgody (art. 6 ust. 1 lit. a RODO)

§4. ODBIORCY DANYCH

Twoje dane mogą być przekazywane:
- Organom państwowym (urzędy skarbowe, ZUS)
- Dostawcom usług IT
- Podmiotom przetwarzającym płatności
- Kancelariom prawnym i księgowym

§5. OKRES PRZECHOWYWANIA

Dane przechowujemy przez:
- Dane księgowe: 5 lat od końca roku podatkowego
- Dane kadrowe: 10 lat od zakończenia współpracy
- Dane marketingowe: do wycofania zgody

§6. TWOJE PRAWA

Masz prawo do:
✓ Dostępu do danych
✓ Sprostowania danych
✓ Usunięcia danych
✓ Ograniczenia przetwarzania
✓ Przenoszenia danych
✓ Sprzeciwu wobec przetwarzania
✓ Wycofania zgody
✓ Skargi do UODO

§7. BEZPIECZEŃSTWO

Stosujemy środki bezpieczeństwa:
- Szyfrowanie SSL/TLS
- Szyfrowanie danych w bazie
- Kontrola dostępu
- Regularne kopie zapasowe
- Monitoring bezpieczeństwa

§8. PLIKI COOKIES

Używamy cookies w celu:
- Utrzymania sesji użytkownika
- Analizy ruchu (analytics)
- Personalizacji treści

Możesz zarządzać cookies w ustawieniach przeglądarki.

§9. KONTAKT

W sprawach ochrony danych:
Email: privacy@workifypl.com

========================================
WORKIFYPL SP. Z O.O.
`;

// ============================================
// UMOWA LICENCYJNA - PRAWA AUTORSKIE (załącznik do umowy o dzieło)
// ============================================
const LICENSE_AUTHOR_CONTENT = `UMOWA LICENCYJNA / PRZENIESIENIE PRAW AUTORSKICH
========================================

Załącznik do Umowy o dzieło nr _____________

zawarta w dniu _____________ w _____________

pomiędzy:

ZAMAWIAJĄCY/LICENCJOBIORCA:
_________________________________________________
NIP: ___________________
Adres: __________________________________________

a

WYKONAWCA/LICENCJODAWCA:
_________________________________________________
PESEL: ___________________
Adres: __________________________________________


§1. PRZEDMIOT UMOWY

1. Przedmiotem niniejszej umowy jest przeniesienie autorskich praw majątkowych 
   do utworu (dzieła) stworzonego w ramach Umowy o dzieło, o której mowa w nagłówku.

2. Utwór obejmuje: _________________________________________________
   _________________________________________________________________

§2. PRZENIESIENIE PRAW

1. Wykonawca przenosi na Zamawiającego całość autorskich praw majątkowych 
   do Utworu, bez ograniczeń czasowych i terytorialnych.

2. Przeniesienie praw następuje z chwilą podpisania protokołu zdawczo-odbiorczego 
   i zapłaty wynagrodzenia.

3. Przeniesienie obejmuje następujące pola eksploatacji:

   a) UTRWALANIE I ZWIELOKROTNIANIE
      - utrwalanie na wszelkich nośnikach
      - zwielokrotnianie dowolną techniką
      - wprowadzanie do pamięci komputera
      - przechowywanie w chmurze

   b) OBRÓT ORYGINAŁEM I EGZEMPLARZAMI
      - wprowadzanie do obrotu
      - użyczanie
      - najem
      - dzierżawa

   c) ROZPOWSZECHNIANIE
      - publiczne wykonanie
      - publiczne wyświetlanie
      - publiczne odtwarzanie
      - nadawanie
      - reemitowanie
      - udostępnianie w Internecie

   d) MODYFIKACJE
      - prawo do opracowań
      - prawo do tłumaczeń
      - prawo do adaptacji
      - prawo do przeróbek

§3. PRAWA ZALEŻNE

1. Wykonawca wyraża zgodę na wykonywanie praw zależnych do Utworu 
   przez Zamawiającego lub osoby przez niego upoważnione.

2. Zamawiający ma prawo do:
   a) modyfikowania Utworu
   b) łączenia z innymi utworami
   c) wykorzystania fragmentów
   d) tworzenia dzieł pochodnych

§4. PRAWA OSOBISTE

1. Wykonawca zobowiązuje się do niewykonywania autorskich praw osobistych 
   w zakresie:
   a) decydowania o pierwszym udostępnieniu Utworu
   b) nadzoru nad sposobem korzystania z Utworu

2. Wykonawca upoważnia Zamawiającego do:
   a) decydowania o oznaczeniu Utworu nazwiskiem lub pseudonimem Wykonawcy
   b) decydowania o udostępnianiu Utworu anonimowo

§5. WYNAGRODZENIE

1. Wynagrodzenie za przeniesienie praw autorskich jest zawarte 
   w wynagrodzeniu określonym w Umowie o dzieło.

2. Wynagrodzenie obejmuje wszystkie pola eksploatacji wymienione w §2.

3. Wykonawcy nie przysługuje dodatkowe wynagrodzenie z tytułu 
   eksploatacji Utworu.

§6. OŚWIADCZENIA I GWARANCJE

Wykonawca oświadcza i gwarantuje, że:

a) Jest jedynym twórcą Utworu
b) Utwór jest oryginalny i nie narusza praw osób trzecich
c) Prawa do Utworu nie są obciążone prawami osób trzecich
d) Utwór nie był wcześniej publikowany
e) Nie udzielił licencji na korzystanie z Utworu osobom trzecim

§7. ODPOWIEDZIALNOŚĆ

1. Wykonawca ponosi odpowiedzialność za naruszenie praw osób trzecich 
   w związku z Utworem.

2. W przypadku roszczeń osób trzecich, Wykonawca zobowiązuje się do:
   a) zwolnienia Zamawiającego z odpowiedzialności
   b) pokrycia kosztów obrony prawnej
   c) naprawienia szkody

§8. KARY UMOWNE

1. W przypadku naruszenia przez Wykonawcę postanowień niniejszej umowy, 
   Zamawiającemu przysługuje kara umowna w wysokości 200% wynagrodzenia.

2. Zamawiający może dochodzić odszkodowania przewyższającego karę umowną.

§9. POSTANOWIENIA KOŃCOWE

1. Umowa wchodzi w życie z dniem podpisania.

2. Wszelkie zmiany wymagają formy pisemnej.

3. W sprawach nieuregulowanych stosuje się przepisy:
   - Ustawy o prawie autorskim i prawach pokrewnych
   - Kodeksu cywilnego

4. Spory rozstrzygane będą przez sąd właściwy dla siedziby Zamawiającego.

5. Umowa sporządzona w dwóch jednobrzmiących egzemplarzach.


_____________________________          _____________________________
         ZAMAWIAJĄCY                           WYKONAWCA
    (data, podpis, pieczęć)                  (data, podpis)


========================================
Załącznik do Umowy o dzieło
`;
